import { CSSResult, html, TemplateResult, unsafeCSS } from 'lit';
import { property, query, state } from 'lit/decorators.js';

import { styles as globalStyles } from '~/ui/scss/main.styles';
import { styles } from './price-box.styles';

import { classMap } from 'lit/directives/class-map.js';
import '~/ui/components/button';

import { SvgUtility } from '~/ui/utils';

import { Task } from '@lit/task';
import { RoundtripOfferViewModel } from '~/tailormade/interfaces';
import DERModuleElement from '~/modules/utils/der-module-element';
import { RoundtripOfferData } from '../roundtrip-interfaces';

export class PriceBox extends DERModuleElement {
    static override styles: CSSResult[] = [unsafeCSS(globalStyles), unsafeCSS(styles)];

    @property({ type: String, attribute: 'component-title' }) componentTitle: string = '';
    @property({ type: String, attribute: 'analytics-name' }) analyticsName: string = '';
    @property({ type: String, attribute: 'data-url' }) dataUrl: string = '';
    @property({ type: String, attribute: 'fallback-price' }) fallbackPrice: string = '';
    @property({ type: Object, attribute: 'offer-data' }) offerData: RoundtripOfferData = {};
    @property({ type: String, attribute: 'operator-name' }) operatorName: string = '';
    @property({ type: Boolean }) previewMode: boolean = false;
    @property({ type: String, attribute: 'roundtrip-id' }) roundtripId: string = '';
    @property({ type: String, attribute: 'roundtrip-key' }) roundtripKey: string = '';
    @property({ type: String, attribute: 'roundtrip-types' }) roundtripTypes: string = '';

    @query('.m-price-box') priceBoxElement?: HTMLElement;

    @state() bestOffer?: RoundtripOfferViewModel | null = null;

    private _contentTask: Task = new Task(this, {
        task: async () => {
            await this._loadBestOffer();
        },
        args: () => [this.lang, this.roundtripId, this.roundtripKey, this.dataUrl],
    });

    private _onButtonClick(event: PointerEvent): void {
        event.stopPropagation();
        event.preventDefault();

        // Dispatch analytics event first to ensure it propagates before any view changes
        this._dispatchAnalyticsEvent();

        // Then emit the click event
        this.emitEvent('der.click', this.bestOffer);
    }

    /**
     * Dispatches a custom `der-analytics` event containing analytics
     * data related to the user's interaction with the PriceBox.
     *
     * The event includes information such as the event name, element ID,
     * action, category, and related offer or roundtrip metadata.
     *
     * It differentiates between whether a real price is available or
     * only a fallback price is shown, adjusting event naming accordingly.
     *
     */
    private _dispatchAnalyticsEvent(): void {
        // Determine if prices are available (only real prices from bestOffer count, not fallback)
        const hasPrices = this.bestOffer?.price?.formattedStringWithCurrency;

        // Determine event name and action based on price availability
        const eventName = hasPrices ? 'calendar-open' : 'offerrequest-open';
        const action = 'CTA clicked';

        let category = this.previewMode ? 'pricebox_bar' : 'pricebox';

        const analyticsData = {
            offerUri: this.bestOffer?.uri || '',
            roundtripKey: this.roundtripKey,
            name: this.analyticsName,
        };

        /**
         *
         */
        this.dispatchEvent(
            new CustomEvent('der-analytics', {
                detail: {
                    name: eventName,
                    element_name: this.tagName.toLowerCase(),
                    element_id: this.id ?? '',
                    action: action,
                    category: category,
                    data: analyticsData,
                },
                bubbles: true,
                composed: true,
            })
        );
    }

    /**
     * Loads the best available offer data for the current roundtrip from
     * the backend API defined by the `dataUrl` property.
     *
     * It constructs a query string with parameters like language, brand,
     * currency, tenant, roundtrip ID, and roundtrip key. If the request
     * succeeds, it updates the component's `bestOffer` state.
     *
     * This function gracefully handles network errors and missing
     * parameters without throwing exceptions.
     */
    private async _loadBestOffer(): Promise<void> {
        if (!this.roundtripId || !this.roundtripKey || !this.dataUrl) {
            return;
        }
        const params = new URLSearchParams();
        params.append('lang', this.lang);
        params.append('brand', this.brand);
        params.append('currency', this.currency);
        params.append('tenant', this.tenant);
        params.append('roundtripId', this.roundtripId);
        params.append('roundtripKey', this.roundtripKey);
        try {
            const response = await fetch(`${this.dataUrl}?${params}`);
            if (response.ok) {
                const data: RoundtripOfferViewModel = await response.json();
                this.bestOffer = data;
            }
        } catch {
            return;
        }
    }

    /**
     * Main render method of the `PriceBox` component.
     *
     * It leverages a Lit `Task` (`_contentTask`) to manage asynchronous
     * loading and render states (pending, complete, and error).
     *
     * Depending on whether the data is loaded or pending, it renders
     * either a loading skeleton or the full price box layout including
     * the roundtrip information, pricing, and CTA button.
     *
     */
    override render(): TemplateResult | undefined {
        return this._contentTask.render({
            pending: () => html`
                <div
                    class=${classMap({
                        'm-price-box': true,
                        preview: this.previewMode,
                    })}
                >
                    <div class="m-price-box__roundtrip">
                        ${this.componentTitle
                            ? html`<div class="m-price-box__roundtrip-title">${this.componentTitle}</div>`
                            : html``}
                        ${this._renderRoundtripData(true)}
                    </div>
                    <div class="m-price-box__price">
                        <!-- Loading state for price -->
                    </div>
                    <div class="m-price-box__button">
                        <der-ui-button @click=${this._onButtonClick}>${this.trans('priceboxButton')}</der-ui-button>
                        <div class="m-price-box__information">
                            ${this.transRichText('tourOperatorLabel', { operator: this.operatorName })}
                        </div>
                    </div>
                </div>
            `,
            complete: () => html`
                <div
                    class=${classMap({
                        'm-price-box': true,
                        preview: this.previewMode,
                    })}
                >
                    <div class="m-price-box__roundtrip">
                        ${this.componentTitle
                            ? html`<div class="m-price-box__roundtrip-title">${this.componentTitle}</div>`
                            : html``}
                        ${this._renderRoundtripData()}
                    </div>
                    <div class="m-price-box__price">
                        <div class="m-price-box__amount">
                            <span class="m-price-box__label">${this.trans('priceFromLabel')}</span>
                            ${this.bestOffer?.price.formattedStringWithCurrency || this.fallbackPrice}
                        </div>
                        <div class="m-price-box__per-person">${this.trans('perPersonLabel')}</div>
                    </div>
                    <div class="m-price-box__button">
                        <der-ui-button @click=${this._onButtonClick}>${this.trans('priceboxButton')}</der-ui-button>
                        <div class="m-price-box__information">
                            ${this.transRichText('tourOperatorLabel', { operator: this.operatorName })}
                        </div>
                    </div>
                </div>
            `,
            error: (e) => html`<p>Error: ${e}</p>`,
        });
    }

    /**
     * Renders the roundtrip-related data section of the PriceBox component.
     *
     * This section includes visual icons and textual information about:
     * - The roundtrip type (if available)
     * - The duration of the trip (from either `bestOffer` or `offerData`)
     * - The booking code (`roundtripKey`)
     *
     * If the data is still pending (e.g., during loading state), placeholders such as `-`
     * are shown for dynamic values like duration.
     *
     * The method uses SVG icons from the `SvgUtility` module to visually represent
     * each data point.
     *
     */
    private _renderRoundtripData(isPending?: boolean): TemplateResult | undefined {
        return html`<div class="m-price-box__roundtrip-data">
            ${this.roundtripTypes
                ? html`<div>
                      <div class="m-price-box__data-icon">
                          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 40 40" fill="none">
                              ${SvgUtility.systemGroup}
                          </svg>
                      </div>
                      <div class="m-price-box__data-content">
                          <div class="m-price-box__label">${this.trans('roundtripTypeLabel')}</div>
                          ${this.roundtripTypes}
                      </div>
                  </div>`
                : html``}
            ${this.bestOffer?.duration || this.offerData?.duration
                ? html`<div>
                      <div class="m-price-box__data-icon">
                          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 40 40" fill="none">
                              ${SvgUtility.systemClock}
                          </svg>
                      </div>
                      <div class="m-price-box__data-content">
                          <div class="m-price-box__label">${this.trans('durationLabel')}</div>
                          ${isPending
                              ? '-'
                              : html`${this.transRichText('durationData', {
                                    amount: String(this.bestOffer?.duration || this.offerData?.duration || 0),
                                })}`}
                      </div>
                  </div>`
                : html``}
            ${this.roundtripKey
                ? html`<div>
                      <div class="m-price-box__data-icon">
                          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 40 40" fill="none">
                              ${SvgUtility.systemInfoCircle}
                          </svg>
                      </div>
                      <div class="m-price-box__data-content">
                          <div class="m-price-box__label">${this.trans('bookingCodeLabel')}</div>

                          ${this.roundtripKey}
                      </div>
                  </div>`
                : html``}
        </div>`;
    }
}
